import { AddInventory } from '@/apis/inventory';
import { GetLocationsWithPaginationAndSearch } from '@/apis/onboarding';
import type { LocationData } from '@/apis/types';
import { ChevronDownIcon } from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { CustomButton, SearchInput } from '@/components';
import { useSpin } from '@/components/SpinContext';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import { useUnAuthorize } from '@/hooks';
import { Checkbox, Flex, Menu, message, Select, Space, Spin, Tag } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { InventoryDetails } from './InventoryDetailDrawer';
import '../../Shipments/Filters/FilterDropdowns.css';

type InventoryLocationMultiSelectProps = {
  inventoryDetails: InventoryDetails;
  onUpdated?: () => Promise<void> | void;
};

const InventoryLocationMultiSelect = ({
  inventoryDetails,
  onUpdated,
}: InventoryLocationMultiSelectProps) => {
  const { token } = useToken();
  const { unauthorize } = useUnAuthorize();
  const { showSpin, hideSpin } = useSpin();
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [open, setOpen] = useState(false);
  const [defaultSelection, setDefaultSelection] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [defaultSelectAll, setDefaultSelectAll] = useState(false);
  const requestIdRef = useRef(0);

  const ALL_TAG_VALUE = '__ALL__';
  const appliedIds = useMemo(
    () =>
      (inventoryDetails.locations ?? [])
        .map((location) => location.location_id)
        .filter((locationId): locationId is string => Boolean(locationId)),
    [inventoryDetails.locations]
  );

  useEffect(() => {
    const isSelectAll = Boolean(inventoryDetails?.select_all_locations);
    setSelectedIds(isSelectAll ? [] : appliedIds);
    setDefaultSelection(isSelectAll ? [] : appliedIds);
    setSelectAll(isSelectAll);
    setDefaultSelectAll(isSelectAll);
  }, [appliedIds, inventoryDetails?.select_all_locations]);

  const loadLocations = useCallback(
    async (nextPage: number, append = false) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);

      try {
        const response = await GetLocationsWithPaginationAndSearch(
          token,
          false,
          nextPage,
          search
        );
        const data = response?.data?.data;

        if (!data?.is_success) {
          message.error(data?.message || 'Failed to fetch locations');
          return;
        }

        if (requestId !== requestIdRef.current) return;

        const incoming = data.data?.data ?? [];
        setLocations((current) => {
          const combined = append ? [...current, ...incoming] : incoming;
          return combined.filter(
            (location, index, all) =>
              all.findIndex((item) => item.id === location.id) === index
          );
        });
        setPage(nextPage);
        setTotalPages(data.page_count ?? 1);
      } catch (error) {
        unauthorize(error);
        message.error('Failed to fetch locations');
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [search, token, unauthorize]
  );

  useEffect(() => {
    if (!open) return;
    loadLocations(1);
  }, [loadLocations, open]);

  const selectedOptions = useMemo(
    () =>
      (inventoryDetails.locations ?? [])
        .filter((location) => location.location_id)
        .map((location) => ({
          id: location.location_id as string,
          name: location.name || 'Unknown location',
          address: location.address,
        })),
    [inventoryDetails.locations]
  );

  const options = useMemo(() => {
    const allLocations = [
      ...selectedOptions,
      ...locations.map((location) => ({
        id: location.id,
        name: location.name,
        address: location.address,
      })),
    ];

    return allLocations
      .filter(
        (location, index) =>
          allLocations.findIndex((item) => item.id === location.id) === index
      )
      .map((location) => ({
        value: location.id,
        label: location.name,
        address: location.address,
      }));
  }, [locations, selectedOptions]);

  const toNumber = (value?: string | number | null) => {
    const parsed = Number(value ?? 0);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const toOptionalNumber = (value?: string | number | null) => {
    if (value === undefined || value === null || value === '') return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const buildUpdatePayload = (
    nextSelectedIds: string[],
    nextSelectAll: boolean
  ) => {
    const existingLocationsById = new Map(
      (inventoryDetails.locations ?? [])
        .filter((location) => location.location_id)
        .map((location) => [location.location_id as string, location])
    );

    return {
      id: inventoryDetails.id,
      product_id: inventoryDetails.product?.id,
      sku: inventoryDetails.product?.name || inventoryDetails.sku || '',
      description: inventoryDetails.description || '',
      team_ids: (inventoryDetails.teams ?? [])
        .map((team) => team.id)
        .filter(Boolean),
      sub_items: (inventoryDetails.sub_items ?? [])
        .map((item) => ({
          product_id: item.product_id || item.product?.id,
          quantity: Number(item.quantity ?? 0),
        }))
        .filter((item) => item.product_id),
      select_all_locations: nextSelectAll,
      locations: nextSelectAll
        ? []
        : nextSelectedIds.map((locationId) => {
            const existingLocation = existingLocationsById.get(locationId);

            return {
              facility_id: locationId,
              stock: toNumber(existingLocation?.stock),
              reorder_target: toOptionalNumber(
                existingLocation?.reorder_target
              ),
              total_required: toOptionalNumber(
                existingLocation?.total_required
              ),
              is_integration_managed: Boolean(
                existingLocation?.is_integration_managed
              ),
            };
          }),
    };
  };

  const handleApply = async (
    nextSelectedIds = selectedIds,
    nextSelectAll = selectAll
  ) => {
    if (!inventoryDetails.id || !inventoryDetails.product?.id) {
      message.error('Unable to update locations');
      return;
    }

    try {
      setIsApplying(true);
      showSpin();
      const response = await AddInventory(
        token,
        buildUpdatePayload(nextSelectedIds, nextSelectAll)
      );

      if (response?.data?.data?.is_success) {
        setSelectedIds(nextSelectedIds);
        setSelectAll(nextSelectAll);
        setOpen(false);
        message.success(response?.data?.data?.message || 'Locations updated');
        await onUpdated?.();
      } else {
        message.error(
          response?.data?.data?.message || 'Failed to update locations'
        );
      }
    } catch (error) {
      unauthorize(error);
      message.error('Failed to update locations');
    } finally {
      hideSpin();
      setIsApplying(false);
    }
  };

  return (
    <div className='inventory-location-multi-select-shell'>
      <Select<string[]>
        mode='multiple'
        value={selectAll ? [ALL_TAG_VALUE] : selectedIds}
        open={open}
        onOpenChange={(visible) => {
          if (!visible) setSearch('');
          setOpen(visible);
        }}
        placeholder='Select...'
        showSearch={false}
        className='custom-dd-style inventory-location-multi-select'
        popupClassName='inventory-location-select-popup'
        suffixIcon={
          <ChevronDownIcon
            size={16}
            className={`rotate-icon ${open ? 'open' : ''}`}
          />
        }
        tagRender={({ label, value }) => {
          const preventOpen = (event: React.MouseEvent<HTMLSpanElement>) => {
            event.preventDefault();
            event.stopPropagation();
          };
          const isAllTag = value === ALL_TAG_VALUE;

          return (
            <Tag
              className='notification-filter-tag'
              closable
              onMouseDown={preventOpen}
              onClose={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (isAllTag) {
                  setSelectAll(false);
                  setSelectedIds([]);
                } else {
                  setSelectedIds((current) =>
                    current.filter((locationId) => locationId !== value)
                  );
                }
                setOpen(true);
              }}
            >
              <span>{isAllTag ? 'All' : label}</span>
            </Tag>
          );
        }}
        options={options}
        dropdownRender={() => (
          <>
            <div style={{ padding: '8px 16px 4px' }}>
              <SearchInput
                placeholder='Search location'
                width='100%'
                style={{ backgroundColor: 'var(--gray-300)' }}
                onChange={setSearch}
                onClear={() => setSearch('')}
              />
            </div>

            <Menu
              selectable={false}
              style={{ height: 357, overflowY: 'auto' }}
              onScroll={(event) => {
                const target = event.currentTarget;
                const isNearBottom =
                  target.scrollTop + target.clientHeight >=
                  target.scrollHeight - 24;
                if (isNearBottom && !loading && page < totalPages) {
                  loadLocations(page + 1, true);
                }
              }}
            >
              <Menu.Item
                key='__select_all'
                className='gl-select-all-item'
                onClick={(event) => event.domEvent.stopPropagation()}
              >
                <Checkbox
                  className='custom-menu-shipment-checkbox-only gl-checkbox checkbox-sm'
                  checked={
                    selectAll ||
                    (search.trim().length > 0 &&
                      locations.length > 0 &&
                      locations.every((location) =>
                        selectedIds.includes(String(location.id))
                      ))
                  }
                  indeterminate={false}
                  onChange={async (event) => {
                    if (event.target.checked) {
                      // With an active search, fetch every matching page and
                      // add those ids to the selection — never flip the
                      // global select-all flag.
                      if (search.trim()) {
                        if (selectAll) return; // everything already selected
                        setLoading(true);
                        try {
                          let pageNum = 1;
                          let totalPages = 1;
                          const all: LocationData[] = [];
                          do {
                            const response =
                              await GetLocationsWithPaginationAndSearch(
                                token,
                                false,
                                pageNum,
                                search
                              );
                            const payload = response?.data?.data;
                            const chunk = payload?.data?.data ?? [];
                            totalPages = payload?.page_count ?? pageNum;
                            all.push(...chunk);
                            pageNum += 1;
                          } while (pageNum <= totalPages && pageNum <= 500);

                          const seen = new Set<string | number>();
                          const unique = all.filter((location) => {
                            if (seen.has(location.id)) return false;
                            seen.add(location.id);
                            return true;
                          });
                          setLocations(unique);
                          setPage(Math.max(1, totalPages));
                          setTotalPages(totalPages);
                          setSelectedIds((current) =>
                            Array.from(
                              new Set([
                                ...current,
                                ...unique.map((location) =>
                                  String(location.id)
                                ),
                              ])
                            )
                          );
                        } catch (error) {
                          unauthorize(error);
                          message.error('Failed to fetch locations');
                        } finally {
                          setLoading(false);
                        }
                        return;
                      }
                      setSelectAll(true);
                      setSelectedIds([]);
                    } else {
                      // Global "all" can't be partially unchecked per search;
                      // fall back to the original clear-all behavior.
                      if (selectAll || !search.trim()) {
                        setSelectAll(false);
                        setSelectedIds([]);
                        return;
                      }
                      const loadedIds = new Set(
                        locations.map((location) => String(location.id))
                      );
                      setSelectedIds((current) =>
                        current.filter(
                          (locationId) => !loadedIds.has(String(locationId))
                        )
                      );
                    }
                  }}
                >
                  Select All
                </Checkbox>
              </Menu.Item>

              {options.map((option) => (
                <Menu.Item
                  key={option.value}
                  className='gl-menu-item gl-single-item'
                  onClick={(event) => event.domEvent.stopPropagation()}
                >
                  <Checkbox
                    className='custom-menu-shipment-checkbox-only gl-checkbox checkbox-sm'
                    checked={selectAll || selectedIds.includes(option.value)}
                    onChange={(event) => {
                      if (selectAll) {
                        setSelectAll(false);
                        setSelectedIds(
                          event.target.checked
                            ? options.map((item) => item?.value)
                            : options
                                .map((item) => item?.value)
                                .filter(
                                  (locationId) => locationId !== option?.value
                                )
                        );
                        return;
                      }

                      setSelectedIds((current) =>
                        current.includes(option.value)
                          ? current.filter(
                              (locationId) => locationId !== option.value
                            )
                          : [...current, option.value]
                      );
                    }}
                  >
                    <div className='gl-text-wrapper'>
                      <TruncateWithTooltip
                        text={option.label}
                        max={25}
                        render={(text) => (
                          <div className='gl-truncate'>
                            <p className='gl-name'>{text}</p>
                          </div>
                        )}
                      />
                      {option.address ? (
                        <TruncateWithTooltip
                          text={option.address}
                          max={40}
                          render={(text) => (
                            <div className='gl-truncate'>
                              <p className='gl-address'>{text}</p>
                            </div>
                          )}
                        />
                      ) : null}
                    </div>
                  </Checkbox>
                </Menu.Item>
              ))}
            </Menu>

            {loading ? (
              <Flex justify='center' style={{ padding: 8 }}>
                <Spin size='small' />
              </Flex>
            ) : null}

            <Space
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '4px 12px',
              }}
            >
              <CustomButton
                text='Reset'
                onClick={() => {
                  setSelectedIds(defaultSelection);
                  setSelectAll(defaultSelectAll);
                }}
                width='52px'
                height='28px'
                bgColor='transparent'
                color='var(--text-primary)'
                fontSize='12px'
                fontWeight={600}
                borderRadius='0px'
                boxShadow='none'
              />
              <CustomButton
                text='Apply'
                onClick={() => handleApply()}
                disabled={isApplying}
                width='52px'
                height='28px'
                bgColor='var(--text-primary)'
                color='var(--surface-base)'
                fontSize='12px'
                fontWeight={500}
                borderRadius='8px'
                boxShadow='0px 4px 16px 0px rgba(0, 0, 0, 0.27)'
              />
            </Space>
          </>
        )}
      />
    </div>
  );
};

export default InventoryLocationMultiSelect;
